import React from 'react';
import URI from 'urijs';
import { ICONS } from 'Components/utils/icon/icons';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { t } from 'Src/i18n';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { useAppContext } from 'Context/ReactAppContext';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ProductBlockHeader } from 'Components/widgets/product-block-header/ProductBlockHeader';
import { B2BProposalPdfDownloadButton } from 'Components/widgets/payment-info/b2b-payment-info/B2BProposalPdfDownloadButton';
type Props = {
  actions: Array<{
    display: boolean;
    iconName: IconName;
    id: string;
    label: string;
    onClick: () => void;
  }>;
  product: Product;
  proposal: Proposal;
};
export const HeaderLite = ({ actions, product, proposal }: Props) => {
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const isJoyview = !!product?.isJoyview;

  const trackNavigation = () =>
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_HEADER, {
      detail_click: 'back_resort_page',
    });

  // Build home URL with locale if not default locale
  const isDefaultLocale = appContext.locale === appContext.defaultLocale;
  const homeUrl = isDefaultLocale
    ? '/'
    : new URI('/').addQuery('locale', appContext.locale).toString();
  return (
    <header className="bg-white" role="banner">
      <div className="relative m-auto h-auto max-w-site md:flex md:justify-between lg:h-60">
        <div className="relative flex items-center border-b-1 border-grey px-20 py-10 md:border-b-0 md:ps-0">
          <ProductBlockHeader
            hasBackIcon={!appContext.isSeller}
            onClick={trackNavigation}
            product={product}
            queryParamsToIgnore={[
              'proposalIds',
              'selectedProposalId',
              'fromSearches',
              'coderesort',
            ]}
            theme="headerLite"
          />
        </div>

        <a
          rel="noopener noreferrer"
          className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform cursor-pointer self-center md:block"
          href={homeUrl}
        >
          <Icon
            className={isJoyview ? 'text-blueWave' : 'text-black'}
            data-testid="cm-logo"
            isNotSquare
            name={isJoyview ? ICONS.CLUBMED_JOYVIEW : ICONS.CLUBMED}
            width="9.125rem"
          />
        </a>

        <div className="flex items-center justify-between px-20 py-10 md:px-0 md:py-0">
          <p
            className="truncate font-sans text-14 font-bold md:px-20"
            data-testid="reference-number"
          >
            {[t('common.reference'), proposal.id].join(' ')}
          </p>
          <div className="flex border-grey md:items-center md:self-stretch md:border-s-1 md:px-10">
            {actions
              .filter((action) => action.display)
              .map(({ iconName, id, label, onClick }) => (
                <button
                  className="mx-10 flex flex-col items-center text-12 font-bold text-black"
                  data-testid={`HeaderLite-action-btn-${label}`}
                  key={id}
                  onClick={onClick}
                  type="button"
                >
                  <Icon className="h-auto" name={iconName} width="1.25rem" />
                  <span className="sr-only md:not-sr-only">{label}</span>
                </button>
              ))}
            <B2BProposalPdfDownloadButton proposal={proposal} resort={product} />
          </div>
        </div>
      </div>
    </header>
  );
};
